// Smart Daara - Studio IA (profs/admin) : analyse de cours, génération de devoirs (F1), fiches de révision (F4)
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI, aiAPI, downloadBlob } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Sparkles, Trash2, FileDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AIStudio() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [numQ, setNumQ] = useState(10);
  const [difficulty, setDifficulty] = useState('moyen');
  const [questions, setQuestions] = useState(null);
  const [quizTitle, setQuizTitle] = useState('');
  const [sheet, setSheet] = useState(null);

  useEffect(() => {
    courseAPI.listCourses().then((r) => {
      const cs = r.data || [];
      setCourses(cs);
      if (cs.length) setCourseId(cs[0].id);
    }).catch(() => {});
  }, []);

  const loadContent = () => courseId && aiAPI.courseContent(courseId).then((r) => setContent(r.data)).catch(() => {});
  useEffect(() => { setQuestions(null); setSheet(null); loadContent(); if (courseId) aiAPI.getSheet(courseId).then((r) => r.data.exists !== false && setSheet(r.data)).catch(() => {}); }, [courseId]);

  useEffect(() => {
    if (content?.status !== 'pending') return;
    const t = setInterval(loadContent, 5000);
    return () => clearInterval(t);
  }, [content?.status]);

  const analyze = async (force = false) => {
    try {
      const r = await aiAPI.analyzeCourse(courseId, force);
      setContent({ ...(content || {}), status: r.data.status });
      if (r.data.status === 'pending') toast.info(fr ? 'Analyse en cours… (vidéos : jusqu\'à 1-2 min)' : 'Analysis in progress…');
    } catch (e) { toast.error(e.response?.data?.detail || 'Assistant IA indisponible'); }
  };

  const genQuiz = async () => {
    setBusy(true);
    try {
      const r = await aiAPI.generateQuiz({ course_id: courseId, num_questions: numQ, difficulty, language });
      setQuestions(r.data.questions);
      setQuizTitle(fr ? 'Devoir généré par IA' : 'AI generated assignment');
    } catch (e) { toast.error(e.response?.data?.detail || 'Assistant IA indisponible'); }
    finally { setBusy(false); }
  };

  const saveQuiz = async () => {
    try {
      const r = await aiAPI.saveQuiz({ course_id: courseId, title_fr: quizTitle, questions, questions_per_attempt: numQ });
      toast.success(`${fr ? 'Devoir enregistré' : 'Assignment saved'} (${r.data.saved} questions)`);
      setQuestions(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const genSheet = async () => {
    setBusy(true);
    try {
      const r = await aiAPI.generateSheet({ course_id: courseId, language });
      setSheet(r.data);
      toast.success(fr ? 'Fiche générée — relisez puis publiez' : 'Sheet generated — review then publish');
    } catch (e) { toast.error(e.response?.data?.detail || 'Assistant IA indisponible'); }
    finally { setBusy(false); }
  };

  const statusBadge = { none: ['bg-slate-100 text-slate-500', fr ? 'Non analysé' : 'Not analyzed'],
    pending: ['bg-amber-100 text-amber-700', fr ? 'Analyse en cours…' : 'Analyzing…'],
    ready: ['bg-emerald-100 text-emerald-700', fr ? 'Prêt pour l\'IA' : 'AI-ready'],
    failed: ['bg-red-100 text-red-700', fr ? 'Échec — réessayez ou vérifiez le support' : 'Failed'] };
  const st = content?.status || 'none';

  return (
    <div className="space-y-6" data-testid="ai-studio-page">
      <h1 className="text-2xl font-outfit font-bold text-slate-900 flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-emerald-600" />{fr ? 'Studio IA' : 'AI Studio'}
      </h1>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">{fr ? 'Cours' : 'Course'}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block min-w-[260px]" value={courseId}
            onChange={(e) => setCourseId(e.target.value)} data-testid="ai-course-select">
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title_fr || c.title}</option>)}
          </select>
        </div>
        <Badge className={statusBadge[st][0]} data-testid="ai-content-status">{statusBadge[st][1]}</Badge>
        {st !== 'pending' && (
          <Button variant="outline" onClick={() => analyze(st === 'ready' || st === 'failed')} data-testid="prepare-ai-btn">
            <RefreshCw className="w-4 h-4 mr-2" />{st === 'ready' ? (fr ? 'Réanalyser' : 'Re-analyze') : (fr ? 'Préparer pour l\'IA' : 'Prepare for AI')}
          </Button>
        )}
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? '✨ Générer un devoir (QCM)' : '✨ Generate an assignment (MCQ)'}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">{fr ? 'Nombre de questions' : 'Questions'}</Label>
              <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block" value={numQ} onChange={(e) => setNumQ(parseInt(e.target.value))} data-testid="ai-numq-select">
                <option value={10}>10</option><option value={20}>20</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">{fr ? 'Difficulté' : 'Difficulty'}</Label>
              <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                <option value="facile">{fr ? 'Facile' : 'Easy'}</option>
                <option value="moyen">{fr ? 'Moyen' : 'Medium'}</option>
                <option value="difficile">{fr ? 'Difficile' : 'Hard'}</option>
              </select>
            </div>
            <Button className="bg-emerald-700" disabled={busy || st !== 'ready'} onClick={genQuiz} data-testid="gen-quiz-btn">
              <Sparkles className="w-4 h-4 mr-2" />{busy ? (fr ? 'Génération…' : 'Generating…') : (fr ? 'Générer' : 'Generate')}
            </Button>
          </div>

          {questions && (
            <div className="space-y-3" data-testid="quiz-preview">
              <div className="flex flex-wrap gap-2 items-center">
                <Input className="max-w-xs" value={quizTitle} onChange={(e) => setQuizTitle(e.target.value)} data-testid="quiz-title-input" />
                <Button className="bg-emerald-700" onClick={saveQuiz} data-testid="save-ai-quiz-btn">{fr ? 'Enregistrer comme devoir' : 'Save as assignment'}</Button>
                <span className="text-xs text-slate-400">{questions.length} questions — {fr ? 'relisez avant d\'enregistrer' : 'review before saving'}</span>
              </div>
              {questions.map((q, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between gap-2">
                    <p className="font-medium text-sm">{i + 1}. {q.question}</p>
                    <button className="text-red-400 hover:text-red-600" onClick={() => setQuestions(questions.filter((_, j) => j !== i))} data-testid={`del-q-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1 text-xs text-slate-600">
                    {['a', 'b', 'c', 'd'].map((o) => (
                      <p key={o} className={q.correct_answer === o ? 'text-emerald-700 font-semibold' : ''}>{o}) {q[`option_${o}`]}</p>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400">💡 {q.explanation} <Badge variant="outline" className="text-[10px] ml-1">{q.notion}</Badge></p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? '📄 Fiche de révision' : '📄 Revision sheet'}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={busy || st !== 'ready'} onClick={genSheet} data-testid="gen-sheet-btn">
              <Sparkles className="w-4 h-4 mr-2" />{sheet ? (fr ? 'Régénérer' : 'Regenerate') : (fr ? 'Générer la fiche' : 'Generate sheet')}
            </Button>
            {sheet && (
              <>
                <Button className={sheet.published ? 'bg-slate-500' : 'bg-emerald-700'} onClick={async () => {
                  const r = await aiAPI.updateSheet(courseId, { published: !sheet.published });
                  setSheet(r.data);
                  toast.success(r.data.published ? (fr ? 'Fiche publiée — visible par les élèves' : 'Published') : (fr ? 'Fiche dépubliée' : 'Unpublished'));
                }} data-testid="publish-sheet-btn">
                  {sheet.published ? (fr ? 'Dépublier' : 'Unpublish') : (fr ? 'Publier pour les élèves' : 'Publish for students')}
                </Button>
                <Button variant="outline" onClick={async () => downloadBlob((await aiAPI.sheetPdf(courseId)).data, 'fiche_revision.pdf')}>
                  <FileDown className="w-4 h-4 mr-2" />PDF
                </Button>
              </>
            )}
          </div>
          {sheet && (
            <div className="space-y-2 text-sm" data-testid="sheet-preview">
              <Textarea value={sheet.content.resume} rows={3} onChange={(e) => setSheet({ ...sheet, content: { ...sheet.content, resume: e.target.value } })}
                onBlur={() => aiAPI.updateSheet(courseId, { content: sheet.content })} />
              <p><b>{fr ? 'Notions' : 'Notions'} :</b> {sheet.content.notions?.join(', ')}</p>
              {sheet.content.definitions?.map((d, i) => <p key={i} className="text-slate-600">• <b>{d.terme}</b> : {d.definition}</p>)}
              <p className="text-slate-600"><b>{fr ? 'Auto-évaluation' : 'Self-check'} :</b> {sheet.content.questions_auto?.join(' — ')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
